import {
	Activity,
	Edit3,
	FileText,
	FolderOpen,
	Hash,
	LogOut,
	Shield,
	User,
	Users,
	Workflow,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { RoleBadge } from './RoleBadge';
import { CompactThemeToggle } from './ThemeToggle';

/**
 * Main navigation bar component for the application
 */
export function NavigationBar() {
	const location = useLocation();
	const { isAuthenticated, user, logout, hasRole, isAdmin } = useAuth();
	const [showUserMenu, setShowUserMenu] = useState(false);

	/**
	 * Navigation items with their routes and icons
	 * Filtered based on user roles and permissions
	 */
	const allNavItems = [
		{
			path: '/flows',
			label: 'Workflows',
			icon: Workflow,
			description: 'Manage saved workflows',
			requiredRole: 'READ',
		},
		{
			path: '/folders',
			label: 'Folders',
			icon: FolderOpen,
			description: 'Organize workflows',
			requiredRole: 'READ',
		},
		{
			path: '/tags',
			label: 'Tags',
			icon: Hash,
			description: 'Manage tags',
			requiredRole: 'WRITE', // Only WRITE+ users can manage tags
		},
		{
			path: '/executions',
			label: 'Executions',
			icon: Activity,
			description: 'View execution history',
			requiredRole: 'READ',
		},
		{
			path: '/editor',
			label: 'Editor',
			icon: Edit3,
			description: 'Create new workflow',
			requiredRole: 'WRITE', // Only WRITE+ users can create workflows
		},
	];

	// Filter navigation items based on user roles
	const navItems = allNavItems.filter(item => {
		if (!item.requiredRole) return true;
		return hasRole(item.requiredRole) || isAdmin();
	});

	/**
	 * Check if a path is currently active
	 */
	const isActive = (path: string) => {
		if (path === '/flows') {
			return location.pathname === '/flows' || location.pathname === '/';
		}
		return location.pathname.startsWith(path);
	};

	return (
		<nav className="bg-white border-b border-gray-200 transition-colors dark:bg-gray-800 dark:border-gray-700">
			<div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
				<div className="flex justify-between h-16">
					<div className="flex items-center space-x-8">
						{/* Logo/Brand */}
						<div className="flex-shrink-0">
							<Link
								to="/flows"
								className="flex items-center space-x-3 text-xl font-bold text-teal-600 transition-colors dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
							>
								<img
									src="/logo.svg"
									alt="Company Logo"
									className="w-12 h-12 rounded-full"
								/>
								<span>Workflow Engine</span>
							</Link>
						</div>

						{/* Navigation Links */}
						<div className="hidden space-x-8 md:flex">
							{navItems.map(item => {
								const Icon = item.icon;
								const active = isActive(item.path);

								return (
									<Link
										key={item.path}
										to={item.path}
										className={`
                      inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 transition-colors
                      ${
							active
								? 'text-blue-600 border-blue-500 dark:text-blue-400 dark:border-blue-400'
								: 'text-gray-500 border-transparent dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
						}
                    `}
										title={item.description}
									>
										<Icon className="mr-2 w-4 h-4" />
										{item.label}
									</Link>
								);
							})}
						</div>
					</div>

					{/* Right side - User menu and Theme toggle */}
					<div className="flex items-center space-x-4">
						{/* User menu (only show when authenticated) */}
						{isAuthenticated && user && (
							<div className="relative">
								<button
									onClick={() => setShowUserMenu(!showUserMenu)}
									className="flex items-center p-1 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 hover:bg-gray-100 dark:hover:bg-gray-700"
									title={`${user.name || user.email} (${
										user.roles?.join(', ') || 'User'
									})`}
								>
									{user.picture ? (
										<img
											className="w-8 h-8 rounded-full"
											src={user.picture}
											alt={user.name || user.email}
										/>
									) : (
										<div className="flex justify-center items-center w-8 h-8 bg-blue-500 rounded-full">
											<User className="w-4 h-4 text-white" />
										</div>
									)}
								</button>

								{/* User dropdown menu */}
								{showUserMenu && (
									<div className="absolute right-0 z-50 mt-2 w-64 bg-white rounded-md ring-1 ring-black ring-opacity-5 shadow-lg dark:bg-gray-800">
										<div className="py-1">
											<div className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200 dark:text-gray-300 dark:border-gray-600">
												<div className="font-medium truncate">
													{user.name || 'User'}
												</div>
												<div className="mb-2 text-xs text-gray-500 truncate dark:text-gray-400">
													{user.email}
												</div>
												<div className="flex flex-wrap gap-1 items-center">
													{user.roles?.map(role => (
														<RoleBadge
															key={role}
															role={role}
															size="xs"
															showTooltip={false}
														/>
													))}
												</div>
											</div>

											{/* Admin links for ADMIN users */}
											{isAdmin() && (
												<>
													<Link
														to="/admin/users"
														onClick={() => setShowUserMenu(false)}
														className="flex items-center px-4 py-2 w-full text-sm text-gray-700 transition-colors dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
													>
														<Users className="mr-2 w-4 h-4" />
														Manage Users
													</Link>
													<Link
														to="/admin/security"
														onClick={() => setShowUserMenu(false)}
														className="flex items-center px-4 py-2 w-full text-sm text-gray-700 transition-colors dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
													>
														<Shield className="mr-2 w-4 h-4" />
														Security Dashboard
													</Link>
													<Link
														to="/admin/audit"
														onClick={() => setShowUserMenu(false)}
														className="flex items-center px-4 py-2 w-full text-sm text-gray-700 transition-colors dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
													>
														<FileText className="mr-2 w-4 h-4" />
														Audit Logs
													</Link>
												</>
											)}

											<button
												onClick={async () => {
													setShowUserMenu(false);
													await logout();
												}}
												className="flex items-center px-4 py-2 w-full text-sm text-gray-700 transition-colors dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
											>
												<LogOut className="mr-2 w-4 h-4" />
												Sign out
											</button>
										</div>
									</div>
								)}
							</div>
						)}

						<CompactThemeToggle />
					</div>
				</div>

				{/* Mobile Navigation Menu */}
				<div className="md:hidden">
					<div className="pt-2 pb-3 space-y-1">
						{navItems.map(item => {
							const Icon = item.icon;
							const active = isActive(item.path);

							return (
								<Link
									key={item.path}
									to={item.path}
									className={`
                    flex items-center pl-3 pr-4 py-2 text-base font-medium border-l-4 transition-colors
                    ${
						active
							? 'text-blue-700 bg-blue-50 border-blue-500 dark:bg-blue-900/20 dark:text-blue-300'
							: 'text-gray-600 border-transparent dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
					}
                  `}
									title={item.description}
								>
									<Icon className="mr-3 w-5 h-5" />
									{item.label}
								</Link>
							);
						})}
					</div>
				</div>
			</div>
		</nav>
	);
}
